import { useState, useEffect, useCallback } from 'react';
import { Joyride, STATUS, type Step, type EventData, type Controls } from 'react-joyride';

const STORAGE_KEY = 'byte_onboarding_complete';

const steps: Step[] = [
  {
    target: '[data-tour="sidebar-nav"]',
    content:
      'Browse modules by section — Restaurants, Users, Content, MicroApps, Query Explorer, and Admin are all accessible from the sidebar.',
    title: 'Sidebar Navigation',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '[data-tour="env-switcher"]',
    content:
      'Switch between Dev, Staging, and Production environments. All data and actions are scoped to the selected environment.',
    title: 'Environment Switcher',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '[data-tour="search-bar"]',
    content:
      'Search across restaurants, users, and content — or press Cmd+K to open search from anywhere.',
    title: 'Global Search',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '[data-tour="first-restaurant-link"]',
    content:
      'Look up restaurant data including equipment, routines, timezones, and configuration details.',
    title: 'Restaurant Lookup',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '[data-tour="admin-section"]',
    content: 'Access the audit log, user management, system health, and platform analytics.',
    title: 'Admin Tools',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '[data-tour="help-section"]',
    content: 'Find guides for every module and restart this tour at any time.',
    title: 'Help & Guides',
    placement: 'right',
    skipBeacon: true,
  },
];

export function useOnboardingTour() {
  const [run, setRun] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      // Delay slightly so layout elements have rendered
      const timer = setTimeout(() => setRun(true), 800);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  const restart = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setRun(true);
  }, []);

  // Listen for restart events from Help page
  useEffect(() => {
    const handler = () => restart();
    window.addEventListener('byte:restart-tour', handler);
    return () => window.removeEventListener('byte:restart-tour', handler);
  }, [restart]);

  const handleEvent = useCallback((data: EventData, _controls: Controls) => {
    const { action } = data;
    // STATUS values correspond to state.status in the event data
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      localStorage.setItem(STORAGE_KEY, 'true');
      setRun(false);
    }
    // Also handle explicit skip action
    if (action === 'skip') {
      localStorage.setItem(STORAGE_KEY, 'true');
      setRun(false);
    }
  }, []);

  return { run, restart, handleEvent };
}

interface OnboardingTourProps {
  run: boolean;
  onEvent: (data: EventData, controls: Controls) => void;
}

export function OnboardingTour({ run, onEvent }: OnboardingTourProps) {
  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      scrollToFirstStep
      onEvent={onEvent}
      options={{
        primaryColor: '#6366f1',
        backgroundColor: '#1e1e2e',
        textColor: '#e0e0e0',
        arrowColor: '#1e1e2e',
        showProgress: true,
        buttons: ['back', 'close', 'primary', 'skip'],
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Done',
        next: 'Next',
        skip: 'Skip tour',
      }}
    />
  );
}
